import React, { useState, useMemo } from 'react';
import { Pencil, Plus, ChevronDown, ChevronUp, X } from 'lucide-react';
import {
    IconButton,
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableHead,
    TableRow,
    CircularProgress,
    Collapse,
    Paper,
    InputAdornment,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import useAccountManagerDelegations from "../../../hooks/purchaseInvoices/useAccountManagerDelegations";
import { useResponsive } from '../../../hooks/purchaseInvoices/useResponsive.js';
import {
    StyledContainer,
    StyledCard,
    StyledTableContainer,
    StyledTableRow,
    StyledTableCell,
    StyledTableHeadCell,
    softColors,
    activeStatusStyles
} from "../../../styles/purchaseInvoices/pages/commonTableStyles.js";
import ConfirmationDialog from '../../../components/purchaseInvoices/pages/ConfirmationDialog.js';
import delegationsStyles from '../../../styles/purchaseInvoices/pages/delegationsStyles.js';

const AccountManagerDelegations = () => {
    const { isDesktop } = useResponsive();
    const [expandedRow, setExpandedRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const {
        delegations,
        isLoading,
        error,
        newDelegation,
        setNewDelegation,
        editingDelegation,
        setEditingDelegation,
        isAddModalOpen,
        setIsAddModalOpen,
        isEditModalOpen,
        setIsEditModalOpen,
        handleAdd,
        handleUpdate,
        handleDeleteClick,
        handleCancelDelete,
        handleConfirmDelete,
        isDeleteDialogOpen,
        delegationToDelete,
        isDeleting,
        handleEdit,
    } = useAccountManagerDelegations();

    const isCurrentlyActive = (delegation) => {
        const now = new Date();
        const startDate = new Date(delegation.startDate);
        const endDate = delegation.endDate ? new Date(delegation.endDate) : null;
        
        return delegation.isActive && 
            now >= startDate && 
            (!endDate || now <= endDate);
    };

    const columns = [
        {
            accessorKey: 'fromAccountManager',
            header: 'מנהלת חשבונות מקורית',
        },
        {
            accessorKey: 'toAccountManager',
            header: 'מוסבת ל',
        },
        {
            accessorKey: 'startDate',
            header: 'מתאריך',
        },
        {
            accessorKey: 'endDate',
            header: 'עד תאריך',
        },
        {
            accessorKey: 'isActive',
            header: 'סטטוס',
        },
        {
            accessorKey: 'notes',
            header: 'הערות',
        },
        {
            id: 'actions',
            header: 'פעולות',
        }
    ];

    const handleEditClick = (delegation) => {
        handleEdit(delegation);
    };

    const handleDeleteClickRow = (delegationId, fromAccountManager) => {
        handleDeleteClick(delegationId, fromAccountManager);
    };

    const renderMobileCard = (delegation) => {
        const isExpanded = expandedRow === delegation._id;

        return (
            <Paper key={delegation._id} elevation={1} sx={delegationsStyles.mobileCard}>
                <Box sx={delegationsStyles.mobileCardHeader}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {delegation.fromAccountManager}
                    </Typography>

                    <Box sx={delegationsStyles.mobileCardButtonsContainer}>
                        <Typography 
                            variant="body2" 
                            sx={isCurrentlyActive(delegation) ? activeStatusStyles.active : activeStatusStyles.inactive}
                        >
                            {isCurrentlyActive(delegation) ? 'פעיל' : 'לא פעיל'}
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="body2" color="text.secondary">מוסבת ל:</Typography>
                    <Typography variant="body1">{delegation.toAccountManager}</Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">מתאריך:</Typography>
                    <Typography variant="body1">
                        {delegation.startDate ? new Date(delegation.startDate).toLocaleDateString('he-IL') : '-'}
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <IconButton
                        size="small"
                        onClick={() => setExpandedRow(isExpanded ? null : delegation._id)}
                        sx={delegationsStyles.expandButton}
                    >
                        {isExpanded ? <ChevronUp /> : <ChevronDown />}
                    </IconButton>
                    <Collapse in={isExpanded} timeout="auto">
                        <Box sx={delegationsStyles.expandedContent}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">עד תאריך:</Typography>
                                <Typography variant="body1">
                                    {delegation.endDate ? new Date(delegation.endDate).toLocaleDateString('he-IL') : 'תמידי'}
                                </Typography>
                            </Box>
                            {delegation.notes && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">הערות:</Typography>
                                    <Typography variant="body1">{delegation.notes}</Typography>
                                </Box>
                            )}
                            <Box display="flex" gap={1} justifyContent="flex-end">
                                <IconButton
                                    aria-label="edit"
                                    onClick={() => handleEditClick(delegation)}
                                    size="small"
                                    sx={delegationsStyles.editButtonIcon}
                                >
                                    <Pencil size={18} color="#1976d2" />
                                </IconButton>
                                <IconButton
                                    aria-label="delete"
                                    onClick={() => handleDeleteClickRow(delegation._id, delegation.fromAccountManager)}
                                    size="small"
                                    sx={delegationsStyles.deleteButtonIcon}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
            </Paper>
        );
    };

    const filteredDelegations = useMemo(() => {
        if (!searchTerm.trim() || !delegations?.delegations?.length) {
            return delegations?.delegations || [];
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        return delegations.delegations.filter(delegation =>
            delegation.fromAccountManager.toLowerCase().includes(lowerSearchTerm) ||
            delegation.toAccountManager.toLowerCase().includes(lowerSearchTerm) ||
            (delegation.notes && delegation.notes.toLowerCase().includes(lowerSearchTerm))
        );
    }, [delegations?.delegations, searchTerm]);

    if (isLoading) {
        return (
            <Box sx={delegationsStyles.loadingContainer}>
                <CircularProgress />
                <Typography variant="h6" color="textSecondary" sx={delegationsStyles.loadingText}>
                    טוען נתונים...
                </Typography>
            </Box>
        );
    }

    return (
        <StyledContainer>
            <StyledCard>
                <Box sx={delegationsStyles.headerBox}>
                    <Box sx={{ width: { xs: '100%', sm: '300px' } }}>
                        <TextField
                            placeholder="חיפוש הסבה"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'primary.main' }} />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ dir: "rtl" }}
                            sx={delegationsStyles.searchInput}
                        />
                    </Box>

                    <Button
                        onClick={() => {
                            setNewDelegation({ 
                                fromAccountManager: "",
                                toAccountManager: "",
                                startDate: "",
                                endDate: "",
                                notes: "",
                                isActive: true
                            });
                            setIsAddModalOpen(true);
                        }}
                        color="primary"
                        size="medium"
                        startIcon={<Plus size={20} strokeWidth={3} />}
                        sx={delegationsStyles.addButtonResponsive}
                    >
                        הוספת הסבה
                    </Button>
                </Box>

                {error ? (
                    <Box sx={delegationsStyles.errorBox}>
                        <Typography variant="h6" color="error" sx={delegationsStyles.errorText}>
                            שגיאה בטעינת הנתונים
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => window.location.reload()}
                        >
                            נסה שנית
                        </Button>
                    </Box>
                ) : isDesktop ? (
                    <StyledTableContainer sx={delegationsStyles.tableMinHeight}>
                        <Table dir="rtl">
                            <TableHead>
                                <TableRow>
                                    {columns.map(column => (
                                        <StyledTableHeadCell
                                            key={column.id || column.accessorKey}
                                            align="center"
                                        >
                                            {column.header}
                                        </StyledTableHeadCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDelegations.length === 0 ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={columns.length} align="center">
                                            <Typography variant="body1" color="text.secondary">
                                                {searchTerm ? "לא נמצאו תוצאות חיפוש" : "אין הסבות להצגה"}
                                            </Typography>
                                        </StyledTableCell>
                                    </TableRow>
                                ) : (
                                    filteredDelegations.map((row) => (
                                        <StyledTableRow key={row._id}>
                                            <StyledTableCell align="center">
                                                {row.fromAccountManager}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {row.toAccountManager}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {row.startDate ? new Date(row.startDate).toLocaleDateString('he-IL') : '-'}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {row.endDate ? new Date(row.endDate).toLocaleDateString('he-IL') : 'תמידי'}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Typography 
                                                    variant="body2" 
                                                    sx={isCurrentlyActive(row) ? activeStatusStyles.active : activeStatusStyles.inactive}
                                                >
                                                    {isCurrentlyActive(row) ? 'כן' : 'לא'}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {row.notes || '-'}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Box display="flex" justifyContent="center" gap={1}>
                                                    <IconButton
                                                        aria-label="edit"
                                                        onClick={() => handleEditClick(row)}
                                                        size="small"
                                                        sx={delegationsStyles.editButtonIcon}
                                                    >
                                                        <Pencil size={18} color="#1976d2" />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label="delete"
                                                        onClick={() => handleDeleteClickRow(row._id, row.fromAccountManager)}
                                                        size="small"
                                                        sx={delegationsStyles.deleteButtonIcon}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                ) : (
                    <Box sx={delegationsStyles.mobileCardContainer}>
                        {filteredDelegations.length === 0 ? (
                            <Box sx={delegationsStyles.emptyBox}>
                                <Typography variant="body1" color="text.secondary">
                                    {searchTerm ? "לא נמצאו תוצאות חיפוש" : "אין הסבות להצגה"}
                                </Typography>
                            </Box>
                        ) : (
                            filteredDelegations.map(delegation => renderMobileCard(delegation))
                        )}
                    </Box>
                )}

                <Box sx={delegationsStyles.footerBox}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        {filteredDelegations.length > 0
                            ? `סה"כ ${filteredDelegations.length} הסבות`
                            : ''
                        }
                    </Typography>
                </Box>
            </StyledCard>

            <Dialog
                open={isAddModalOpen}
                onClose={() => {
                    setNewDelegation({ 
                        fromAccountManager: "",
                        toAccountManager: "",
                        startDate: "",
                        endDate: "",
                        notes: "",
                        isActive: true
                    });
                    setIsAddModalOpen(false);
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: delegationsStyles.dialog }}
            >
                <DialogTitle sx={delegationsStyles.dialogTitle}>
                    <Typography sx={delegationsStyles.dialogTitleText}>
                        הוספת הסבה חדשה
                    </Typography>

                    <IconButton
                        onClick={() => {
                            setNewDelegation({ 
                                fromAccountManager: "",
                                toAccountManager: "",
                                startDate: "",
                                endDate: "",
                                notes: "",
                                isActive: true
                            });
                            setIsAddModalOpen(false);
                        }}
                        sx={delegationsStyles.dialogCloseButton}
                        size="small"
                    >
                        <X size={20} strokeWidth={2.5} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={delegationsStyles.dialogContent}>
                    <div style={delegationsStyles.formContainer}>
                        <div>
                            <label style={delegationsStyles.formLabel}>
                                מנהלת חשבונות מקורית: <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="שם המשתמש בפריוריטי"
                                style={delegationsStyles.formInput}
                                value={newDelegation.fromAccountManager}
                                onChange={(e) => setNewDelegation(prev => ({ ...prev, fromAccountManager: e.target.value }))}
                            />
                        </div>
                        
                        <div>
                            <label style={delegationsStyles.formLabel}>
                                מוסבת ל: <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="שם המשתמש בפריוריטי"
                                style={delegationsStyles.formInput}
                                value={newDelegation.toAccountManager}
                                onChange={(e) => setNewDelegation(prev => ({ ...prev, toAccountManager: e.target.value }))}
                            />
                        </div>

                        <div>
                            <label style={delegationsStyles.formLabel}>
                                תאריך התחלה: <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="date"
                                style={delegationsStyles.formInput}
                                value={newDelegation.startDate}
                                onChange={(e) => setNewDelegation(prev => ({ ...prev, startDate: e.target.value }))}
                            />
                        </div>

                        <div>
                            <label style={delegationsStyles.formLabel}>
                                תאריך סיום:
                            </label>
                            <input
                                type="date"
                                style={delegationsStyles.formInput}
                                value={newDelegation.endDate}
                                onChange={(e) => setNewDelegation(prev => ({ ...prev, endDate: e.target.value }))}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                אם לא צויין - ההסבה תהיה תמידית
                            </Typography>
                        </div>

                        <div>
                            <label style={delegationsStyles.formLabel}>
                                הערות:
                            </label>
                            <textarea
                                placeholder="הערות (אופציונלי)"
                                style={{ ...delegationsStyles.formInput, minHeight: '80px', resize: 'vertical' }}
                                value={newDelegation.notes}
                                onChange={(e) => setNewDelegation(prev => ({ ...prev, notes: e.target.value }))}
                            />
                        </div>

                        <div style={delegationsStyles.dialogActions}>
                            <Button
                                onClick={() => {
                                    setNewDelegation({ 
                                        fromAccountManager: "",
                                        toAccountManager: "",
                                        startDate: "",
                                        endDate: "",
                                        notes: "",
                                        isActive: true
                                    });
                                    setIsAddModalOpen(false);
                                }}
                                sx={delegationsStyles.dialogCancelButton}
                            >
                                ביטול
                            </Button>
                            <Button
                                onClick={async () => {
                                    await handleAdd();
                                }}
                                variant="contained"
                                sx={delegationsStyles.dialogSubmitButton}
                            >
                                הוסף
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: delegationsStyles.dialog }}
            >
                <DialogTitle sx={delegationsStyles.dialogTitle}>
                    <Typography sx={delegationsStyles.dialogTitleText}>
                        עריכת הסבה
                    </Typography>

                    <IconButton
                        onClick={() => setIsEditModalOpen(false)}
                        sx={delegationsStyles.dialogCloseButton}
                        size="small"
                    >
                        <X size={20} strokeWidth={2.5} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={delegationsStyles.dialogContent}>
                    {editingDelegation && (
                        <div style={delegationsStyles.formContainer}>
                            <div>
                                <label style={delegationsStyles.formLabel}>
                                    מנהלת חשבונות מקורית: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="שם המשתמש בפריוריטי"
                                    style={delegationsStyles.formInput}
                                    value={editingDelegation.fromAccountManager}
                                    onChange={(e) => setEditingDelegation(prev => ({ ...prev, fromAccountManager: e.target.value }))}
                                />
                            </div>
                            
                            <div>
                                <label style={delegationsStyles.formLabel}>
                                    מוסבת ל: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="שם המשתמש בפריוריטי"
                                    style={delegationsStyles.formInput}
                                    value={editingDelegation.toAccountManager}
                                    onChange={(e) => setEditingDelegation(prev => ({ ...prev, toAccountManager: e.target.value }))}
                                />
                            </div>

                            <div>
                                <label style={delegationsStyles.formLabel}>
                                    תאריך התחלה: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="date"
                                    style={delegationsStyles.formInput}
                                    value={editingDelegation.startDate}
                                    onChange={(e) => setEditingDelegation(prev => ({ ...prev, startDate: e.target.value }))}
                                />
                            </div>

                            <div>
                                <label style={delegationsStyles.formLabel}>
                                    תאריך סיום:
                                </label>
                                <input
                                    type="date"
                                    style={delegationsStyles.formInput}
                                    value={editingDelegation.endDate}
                                    onChange={(e) => setEditingDelegation(prev => ({ ...prev, endDate: e.target.value }))}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    אם לא צויין - ההסבה תהיה תמידית
                                </Typography>
                            </div>

                            <div>
                                <label style={delegationsStyles.formLabel}>
                                    הערות:
                                </label>
                                <textarea
                                    placeholder="הערות (אופציונלי)"
                                    style={{ ...delegationsStyles.formInput, minHeight: '80px', resize: 'vertical' }}
                                    value={editingDelegation.notes || ""}
                                    onChange={(e) => setEditingDelegation(prev => ({ ...prev, notes: e.target.value }))}
                                />
                            </div>

                            <div style={{ margin: '10px 0' }}>
                                <label style={{ marginLeft: 8 }}>
                                    הסבה פעילה:
                                </label>
                                <input
                                    type="checkbox"
                                    checked={editingDelegation.isActive}
                                    onChange={(e) => setEditingDelegation(prev => ({ ...prev, isActive: e.target.checked }))}
                                    style={{ transform: 'scale(1.2)' }}
                                />
                            </div>

                            <div style={delegationsStyles.dialogActions}>
                                <Button
                                    onClick={() => setIsEditModalOpen(false)}
                                    sx={delegationsStyles.dialogCancelButton}
                                >
                                    ביטול
                                </Button>
                                <Button
                                    onClick={async () => {
                                        await handleUpdate();
                                    }}
                                    variant="contained"
                                    sx={delegationsStyles.dialogSubmitButton}
                                >
                                    עדכון
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                entityName={delegationToDelete?.fromAccountManager || ""}
                entityType="הסבה"
                open={isDeleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                isPending={isDeleting}
            />
        </StyledContainer>
    );
};

export default AccountManagerDelegations;